import { useEffect, useRef, useState } from "react";
import { ChevronDown, Loader2, Check, FileText } from "lucide-react";
import toast from "react-hot-toast";
import { axiosInstance } from "../../lib/axios";

// ── The payment badge on a milestone, made actionable ────────────────────────
//
// BDs could see a milestone's payment state on the projects list but had to
// leave the screen and find the invoice to change it. This turns the badge
// into the control.
//
// It updates the INVOICE, never the badge. A milestone's payment state is
// derived from its invoice — writing the badge directly would leave the two
// disagreeing, and Finance reads the invoice. So each option here maps onto an
// invoice status, and the badge follows once the server answers.

export interface MilestoneInvoice {
  id: string;
  invoiceNumber: string;
  status: string;
  paymentStatus: string;
  amount: number;
  currency: string;
  dueDate: string | null;
}

interface Props {
  paymentState: string;
  invoice: MilestoneInvoice | null;
  // Called after a successful change so the list can refresh.
  onChanged: () => void;
  // Offered when the milestone has no invoice at all.
  onCreateInvoice?: () => void;
  disabled?: boolean;
}

// What a BD can set, and the invoice status each one means. Deliberately
// short: these are the four answers to "where has this money got to", not the
// full invoice lifecycle, which Finance owns.
//
// Each one sends a real invoice status — the endpoint takes `status` and
// nothing else, and derives payment from it (Completed stamps it paid, and
// moving off a paid status re-opens it).
const OPTIONS: { label: string; hint: string; status: string }[] = [
  { label: "Invoice Sent", hint: "Sent to the client, awaiting payment", status: "Submitted" },
  { label: "Paid", hint: "Payment received in full", status: "Completed" },
  { label: "Overdue", hint: "Past its due date and unpaid", status: "Overdue" },
  { label: "Cancelled", hint: "This invoice will not be collected", status: "Cancelled" },
];

const TONE: Record<string, string> = {
  Paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Invoice Sent": "bg-sky-50 text-sky-700 border-sky-200",
  "Invoice Generated": "bg-slate-100 text-slate-700 border-slate-200",
  Overdue: "bg-rose-50 text-rose-700 border-rose-200",
  Cancelled: "bg-slate-100 text-slate-500 border-slate-200",
  Pending: "bg-amber-50 text-amber-700 border-amber-200",
};

const MilestonePaymentPicker = ({ paymentState, invoice, onChanged, onCreateInvoice, disabled }: Props) => {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  // Close on an outside click — the badge sits inside a table row, so a menu
  // left open follows the row around as the list re-renders.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const state = paymentState || "Pending";
  const tone = TONE[state] || TONE.Pending;

  const apply = async (opt: (typeof OPTIONS)[number]) => {
    if (!invoice) return;
    setSaving(opt.label);
    try {
      await axiosInstance.patch(`/invoices/${invoice.id}/status`, { status: opt.status });
      toast.success(`${invoice.invoiceNumber || "Invoice"} marked ${opt.label}.`);
      setOpen(false);
      onChanged();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Couldn't update the invoice");
    } finally {
      setSaving("");
    }
  };

  // No invoice: there is nothing to update, so say that rather than offering
  // options that would quietly write a state Finance has no record of.
  if (!invoice) {
    return (
      <button
        type="button"
        onClick={onCreateInvoice}
        disabled={!onCreateInvoice || disabled}
        title={onCreateInvoice ? "No invoice for this milestone yet — create one" : "No invoice for this milestone yet"}
        className="px-1.5 py-px rounded-full border text-[10px] font-bold bg-slate-100 text-slate-500 border-slate-200 inline-flex items-center gap-1 disabled:cursor-default enabled:hover:bg-slate-200"
      >
        <FileText size={9} /> No invoice
      </button>
    );
  }

  return (
    <div className="relative inline-block" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={disabled}
        title={`${invoice.invoiceNumber || "Invoice"} — ${invoice.status}. Click to update.`}
        className={`px-1.5 py-px rounded-full border text-[10px] font-bold inline-flex items-center gap-0.5 disabled:opacity-50 hover:brightness-95 ${tone}`}
      >
        {state}
        <ChevronDown size={9} />
      </button>

      {open && (
        <div className="absolute z-30 left-0 mt-1 w-60 rounded-xl border border-outline-variant bg-white shadow-xl overflow-hidden">
          <div className="px-3 py-2 border-b border-outline-variant bg-surface-container-low">
            <p className="text-[11px] font-bold text-on-surface">{invoice.invoiceNumber || "Invoice"}</p>
            <p className="text-[10.5px] text-on-surface-variant">
              {invoice.currency} {Number(invoice.amount || 0).toLocaleString()} · currently {invoice.status}
            </p>
          </div>
          {OPTIONS.map((opt) => {
            const current = opt.label === state;
            return (
              <button
                key={opt.label}
                type="button"
                onClick={() => apply(opt)}
                disabled={Boolean(saving) || current}
                className="w-full text-left px-3 py-2 hover:bg-surface-container-low disabled:opacity-60 flex items-start gap-2"
              >
                <span className="w-3 shrink-0 pt-0.5">
                  {saving === opt.label ? (
                    <Loader2 size={11} className="animate-spin text-primary" />
                  ) : current ? (
                    <Check size={11} className="text-emerald-600" />
                  ) : null}
                </span>
                <span className="min-w-0">
                  <span className="block text-[12px] font-semibold text-on-surface">{opt.label}</span>
                  <span className="block text-[10.5px] text-on-surface-variant leading-snug">{opt.hint}</span>
                </span>
              </button>
            );
          })}
          <p className="px-3 py-2 text-[10px] text-on-surface-variant border-t border-outline-variant">
            This updates the invoice. Finance sees the same change.
          </p>
        </div>
      )}
    </div>
  );
};

export default MilestonePaymentPicker;
